import type {
  AgentPresentation,
  AgentPresentationStep,
} from "./agent-presentation-types";
import { agentCursorAnnotationTargets } from "./agent-annotation";
import { rendersInCurrentWindow } from "./agent-presentation-ack";
import { isTauri } from "./is-tauri";
import { useCanvasStore } from "@/stores/canvas-store";

/** Completed action cues are obsolete; explanatory marks keep their lifecycle.
 * Call after completion acknowledgement so verification can observe the cue. */
export function dismissCompletedGuideStep(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  actionSequence: number,
): void {
  if (
    presentation.mode === "operate" ||
    !("user_action" in step) ||
    !step.user_action ||
    (isTauri() && !rendersInCurrentWindow(presentation, step))
  )
    return;
  const annotationId = `${presentation.presentationId}:${step.step_id}`;
  useCanvasStore.setState((state) => {
    const next = new Map(state.agentCursors);
    let changed = false;
    for (const [id, cursor] of next) {
      if (
        cursor.presentationDeliveryId !== presentation.presentationId ||
        cursor.executionId !== presentation.executionId
      )
        continue;
      const targets = agentCursorAnnotationTargets(cursor);
      const hasMark = targets.some(
        (target) => target.annotationId === annotationId && !target.dismissed,
      );
      const ownsPointer =
        step.kind === "point"
          ? cursor.pointTarget?.seq === actionSequence
          : cursor.annotationTarget?.annotationId === annotationId &&
            cursor.pointTarget != null &&
            cursor.pointTarget.seq === cursor.annotationTarget.seq;
      if (!hasMark && !ownsPointer) continue;
      const dismiss = (target: (typeof targets)[number]) =>
        target.annotationId === annotationId
          ? { ...target, dismissed: true }
          : target;
      changed = true;
      next.set(id, {
        ...cursor,
        annotationTargets: targets.map(dismiss),
        annotationTarget:
          cursor.annotationTarget && dismiss(cursor.annotationTarget),
        ...(ownsPointer
          ? {
              pointTarget: undefined,
              presentationNarration: undefined,
              annotationExclusionTargets: undefined,
            }
          : {}),
      });
    }
    return changed ? { agentCursors: next } : state;
  });
}
